<template>
  <div class="home">

    <!-- ===================== HERO — full viewport ===================== -->
    <section class="hero">
      <!-- Three.js low-poly horse -->
      <canvas ref="horseCanvas" class="horse-canvas"></canvas>

      <div class="hero-content">
        <div class="hero-text">
          <div class="hero-badge">{{ localize('HOME_HERO_SUBTITLE') }}</div>
          <h1 class="hero-title">{{ localize('HOME_HERO_TITLE') }}</h1>
          <p class="hero-desc">{{ localize('HOME_HERO_DESC') }}</p>
          <div class="hero-cta-row">
            <div class="cta-choice-wrap">
              <a href="/contact" class="cta-choice cta-choice-defence">
                <span class="cta-choice-scan"></span>
                <span class="cta-choice-label">{{ localize('HOME_CTA_BUTTON') }}</span>
                <span class="cta-choice-hint">{{ localize('HOME_CTA_HINT_DEMO') }}</span>
              </a>
              <div class="cta-reveal">{{ localize('HOME_CTA_REVEAL_DEMO') }}</div>
            </div>
            <div class="cta-choice-wrap">
              <a href="/audit" class="cta-choice cta-choice-offensive">
                <span class="cta-choice-crt"></span>
                <span class="cta-choice-scan"></span>
                <span class="cta-choice-label">{{ localize('HOME_CTA_BUTTON_AUDIT') }}</span>
                <span class="cta-choice-hint">
                  <i class="cta-choice-pulse"></i>{{ localize('HOME_CTA_HINT_AUDIT') }}
                </span>
              </a>
              <div class="cta-reveal cta-reveal-red">{{ localize('HOME_CTA_REVEAL_AUDIT') }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Zero cards — overlap the hero bottom edge -->
      <div class="hero-zeros-wrap">
        <div class="hero-zeros">
          <div class="zero-card" v-for="n in 4" :key="'z'+n">
            <span class="zero-value">{{ localize('HOME_ZERO_VALUE_' + n) }}</span>
            <span class="zero-label">{{ localize('HOME_ZERO_LABEL_' + n) }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================== BUSINESS IMPACT ===================== -->
    <section class="section section-stats">
      <div class="section-wrap">
        <div class="section-badge">{{ localize('HOME_IMPACT_BADGE') }}</div>
        <h2 class="section-title">{{ localize('HOME_IMPACT_TITLE') }}</h2>
        <p class="section-subtitle">{{ localize('HOME_IMPACT_SUBTITLE') }}</p>
        <div class="stat-grid">
          <div class="stat-card" v-for="n in 4" :key="'s'+n">
            <div class="stat-accent"></div>
            <div class="stat-number">{{ localize('HOME_STAT_VALUE_' + n) }}</div>
            <div class="stat-label">{{ localize('HOME_STAT_LABEL_' + n) }}</div>
            <div class="stat-desc">{{ localize('HOME_STAT_DESC_' + n) }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================== BEFORE VS AFTER — gradient band ===================== -->
    <section class="section-full section-purple">
      <div class="section-full-geo">
        <div class="geo geo-a"></div>
        <div class="geo geo-b"></div>
        <div class="geo geo-c"></div>
      </div>
      <div class="section-wrap">
        <div class="section-badge section-badge-light">{{ localize('HOME_COMPARE_BADGE') }}</div>
        <h2 class="section-title section-title-light">{{ localize('HOME_COMPARE_TITLE') }}</h2>
        <div class="compare-grid">
          <div class="compare-col compare-before">
            <div class="compare-header compare-header-before">{{ localize('HOME_COMPARE_BEFORE_TITLE') }}</div>
            <ul class="compare-list">
              <li v-for="n in 6" :key="'b'+n" class="compare-item">
                <span class="compare-bullet bullet-before"></span>
                <span>{{ localize('HOME_COMPARE_BEFORE_' + n) }}</span>
              </li>
            </ul>
          </div>
          <div class="compare-col compare-after">
            <div class="compare-header compare-header-after">{{ localize('HOME_COMPARE_AFTER_TITLE') }}</div>
            <ul class="compare-list">
              <li v-for="n in 6" :key="'a'+n" class="compare-item">
                <span class="compare-bullet bullet-after"></span>
                <span>{{ localize('HOME_COMPARE_AFTER_' + n) }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================== SIMPLICITY ===================== -->
    <section class="section">
      <div class="section-wrap section-wrap-narrow">
        <div class="section-badge">{{ localize('HOME_SIMPLE_BADGE') }}</div>
        <h2 class="section-title">{{ localize('HOME_SIMPLE_TITLE') }}</h2>
        <div class="simplicity-card">
          <div class="simplicity-stripe"></div>
          <p class="simplicity-lead">{{ localize('HOME_SIMPLE_LEAD') }}</p>
          <div class="simplicity-divider"></div>
          <div class="simplicity-points">
            <div class="simplicity-point" v-for="n in 3" :key="'sp'+n">
              <div class="simplicity-bullet"></div>
              <span>{{ localize('HOME_SIMPLE_POINT_' + n) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================== CAPABILITIES — cyan gradient ===================== -->
    <section class="section-full section-cyan">
      <div class="section-full-geo">
        <div class="geo geo-d"></div>
        <div class="geo geo-e"></div>
      </div>
      <div class="section-wrap">
        <div class="section-badge section-badge-light">{{ localize('HOME_CAP_BADGE') }}</div>
        <h2 class="section-title section-title-light">{{ localize('HOME_CAP_TITLE') }}</h2>
        <p class="section-subtitle section-sub-light">{{ localize('HOME_CAP_SUBTITLE') }}</p>
        <div class="cap-grid">
          <div class="cap-card" v-for="cap in capabilities" :key="cap.key">
            <div class="cap-icon" v-html="getRawIcon(cap.icon)"></div>
            <h3 class="cap-name">{{ localize('HOME_CAP_' + cap.key + '_TITLE') }}</h3>
            <p class="cap-desc">{{ localize('HOME_CAP_' + cap.key + '_DESC') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================== DIFFERENTIATION TABLE ===================== -->
    <section class="section">
      <div class="section-wrap">
        <div class="section-badge">{{ localize('HOME_DIFF_BADGE') }}</div>
        <h2 class="section-title">{{ localize('HOME_DIFF_TITLE') }}</h2>
        <div class="diff-card">
          <div class="diff-table">
            <div class="diff-header-row">
              <div class="diff-feature-col"></div>
              <div class="diff-col diff-col-trad">{{ localize('HOME_DIFF_TRADITIONAL') }}</div>
              <div class="diff-col diff-col-enbarr">{{ localize('HOME_DIFF_ENBARR') }}</div>
            </div>
            <div class="diff-row" v-for="n in 7" :key="'d'+n">
              <div class="diff-feature-col">{{ localize('HOME_DIFF_ROW_' + n + '_FEATURE') }}</div>
              <div class="diff-col diff-col-trad">{{ localize('HOME_DIFF_ROW_' + n + '_TRAD') }}</div>
              <div class="diff-col diff-col-enbarr">{{ localize('HOME_DIFF_ROW_' + n + '_ENBARR') }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================== DEPLOYMENT — warm gradient ===================== -->
    <section class="section-full section-warm">
      <div class="section-full-geo">
        <div class="geo geo-f"></div>
        <div class="geo geo-g"></div>
      </div>
      <div class="section-wrap">
        <div class="section-badge section-badge-light">{{ localize('HOME_DEPLOY_BADGE') }}</div>
        <h2 class="section-title section-title-light">{{ localize('HOME_DEPLOY_TITLE') }}</h2>
        <p class="section-subtitle section-sub-light">{{ localize('HOME_DEPLOY_SUBTITLE') }}</p>
        <div class="deploy-grid">
          <div class="deploy-card" v-for="dep in deployments" :key="dep.key">
            <div class="deploy-icon" v-html="getRawIcon(dep.icon)"></div>
            <h3 class="deploy-name">{{ localize('HOME_DEPLOY_' + dep.key + '_TITLE') }}</h3>
            <p class="deploy-desc">{{ localize('HOME_DEPLOY_' + dep.key + '_DESC') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================== CTA ===================== -->
    <section class="section section-cta">
      <div class="section-wrap section-wrap-narrow">
        <h2 class="cta-heading">{{ localize('HOME_CTA_TITLE') }}</h2>
        <p class="cta-subtext">{{ localize('HOME_CTA_QUOTE') }}</p>
        <div class="cta-card">
          <div class="cta-card-geo">
            <div class="cta-geo-1"></div>
            <div class="cta-geo-2"></div>
          </div>
          <div class="cta-button-row">
            <a href="/contact" class="cta-button">{{ localize('HOME_CTA_BUTTON') }}</a>
            <div class="cta-choice-wrap cta-choice-wrap-inline">
              <a href="/audit" class="cta-button cta-button-ghost">{{ localize('HOME_CTA_BUTTON_AUDIT') }}</a>
              <div class="cta-reveal cta-reveal-dark">{{ localize('HOME_CTA_REVEAL_AUDIT') }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>
<script>
import {translationStore} from "@/stores/translation";
import {iconStore} from "@/stores/icon";
import {ref, onMounted, onBeforeUnmount} from "vue";
import * as THREE from "three";
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader.js";

// Warm palette — orange/yellow/pink dominant, with purple/magenta accents
var PALETTE = [
    new THREE.Color(0xfbbf24), // gold
    new THREE.Color(0xf59e0b), // amber
    new THREE.Color(0xf97316), // orange
    new THREE.Color(0xec4899), // magenta/pink
    new THREE.Color(0xfb7185), // rose
    new THREE.Color(0xf43f5e), // hot pink
    new THREE.Color(0xfcd34d), // light gold
    new THREE.Color(0xa855f7), // purple accent
    new THREE.Color(0xf472b6), // light pink
    new THREE.Color(0xfdba74), // peach
];

// Store per-face data: centroid, base color index, original positions
function origamifyModel (model) {
    var lightDir = new THREE.Vector3(1, 1, 1).normalize();
    var normal = new THREE.Vector3(), e1 = new THREE.Vector3(), e2 = new THREE.Vector3();
    var fi = 0, shrink = 0.91;
    var allFaceData = [];

    model.traverse(function (child) {
        if (!child.isMesh) return;
        var geo = child.geometry;
        if (geo.index) { child.geometry = geo.toNonIndexed(); geo = child.geometry; }
        var pos = geo.attributes.position.array;
        var faceCount = pos.length / 9;

        // Shrink faces for origami gaps
        var centroids = [];
        for (var f = 0; f < faceCount; f++) {
            var idx = f * 9;
            var cx = (pos[idx]+pos[idx+3]+pos[idx+6])/3;
            var cy = (pos[idx+1]+pos[idx+4]+pos[idx+7])/3;
            var cz = (pos[idx+2]+pos[idx+5]+pos[idx+8])/3;
            centroids.push(cx, cy, cz);
            for (var vi = 0; vi < 3; vi++) {
                var pi = idx + vi*3;
                pos[pi]   = cx + (pos[pi]  -cx)*shrink;
                pos[pi+1] = cy + (pos[pi+1]-cy)*shrink;
                pos[pi+2] = cz + (pos[pi+2]-cz)*shrink;
            }
        }
        geo.computeVertexNormals();

        // Base color assignment
        var colors = new Float32Array(pos.length);
        var baseIndices = [];
        for (var f = 0; f < faceCount; f++) {
            var idx = f * 9;
            e1.set(pos[idx+3]-pos[idx], pos[idx+4]-pos[idx+1], pos[idx+5]-pos[idx+2]);
            e2.set(pos[idx+6]-pos[idx], pos[idx+7]-pos[idx+1], pos[idx+8]-pos[idx+2]);
            normal.crossVectors(e1, e2).normalize();
            var dot = (normal.dot(lightDir)+1)/2;
            var ci = Math.floor(dot * (PALETTE.length-1));
            ci = (ci + fi) % PALETTE.length;
            fi++;
            baseIndices.push(ci);
            var c = PALETTE[ci];
            for (var v = 0; v < 3; v++) {
                colors[idx+v*3]   = c.r;
                colors[idx+v*3+1] = c.g;
                colors[idx+v*3+2] = c.b;
            }
        }
        geo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
        child.material = new THREE.MeshStandardMaterial({
            vertexColors: true, flatShading: true,
            roughness: 0.28, metalness: 0.06, side: THREE.DoubleSide
        });
        allFaceData.push({ geo: geo, centroids: centroids, baseIndices: baseIndices, faceCount: faceCount });
    });
    return allFaceData;
}

// Color shimmer + sparkle flashes directly on faces
function shimmerFaces (faceDataArr, time) {
    var tmpColor = new THREE.Color();
    for (var d = 0; d < faceDataArr.length; d++) {
        var fd = faceDataArr[d];
        var colors = fd.geo.attributes.color.array;
        for (var f = 0; f < fd.faceCount; f++) {
            var cx = fd.centroids[f*3], cy = fd.centroids[f*3+1], cz = fd.centroids[f*3+2];

            // Very slow color wave
            var wave = Math.sin(cx*1.5 + cy*1.0 + cz*0.8 + time*0.3) * 0.5 + 0.5;
            var shiftedIdx = (fd.baseIndices[f] + Math.floor(wave * 2)) % PALETTE.length;
            tmpColor.lerpColors(PALETTE[fd.baseIndices[f]], PALETTE[shiftedIdx], wave * 0.35);

            // Sparkle — bright flash that exceeds 1.0 for tone-mapped bloom glow
            var s1 = Math.pow(Math.max(0, Math.sin(cx*13.7 + cy*27.3 + time*0.7)), 50);
            var s2 = Math.pow(Math.max(0, Math.sin(cx*9.1 + cz*21.4 + time*0.5)), 45);
            var sparkle = Math.max(s1, s2);
            tmpColor.r = tmpColor.r + sparkle * 2.5;
            tmpColor.g = tmpColor.g + sparkle * 2.0;
            tmpColor.b = tmpColor.b + sparkle * 1.0;

            var idx = f * 9;
            for (var v = 0; v < 3; v++) {
                colors[idx+v*3]   = tmpColor.r;
                colors[idx+v*3+1] = tmpColor.g;
                colors[idx+v*3+2] = tmpColor.b;
            }
        }
        fd.geo.attributes.color.needsUpdate = true;
    }
}


export default {
    "name": "Home",
    setup () {
        const translation = translationStore(),
            icon = iconStore(),
            getRawIcon = function (i) { return icon.get(i); },
            capabilities = [
                {"key": "DETECT", "icon": "ICON_SHIELD"},
                {"key": "AI", "icon": "ICON_BRAIN"},
                {"key": "NETWORK", "icon": "ICON_NETWORK"},
                {"key": "STACK", "icon": "ICON_LAYERS"},
                {"key": "SOVEREIGNTY", "icon": "ICON_LOCK"},
                {"key": "RESPONSE", "icon": "ICON_BOLT"}
            ],
            deployments = [
                {"key": "CLOUD", "icon": "ICON_GLOBE"},
                {"key": "ONPREM", "icon": "ICON_DATABASE"},
                {"key": "HYBRID", "icon": "ICON_LINK"}
            ];

        var horseCanvas = ref(null),
            animId = null,
            renderer = null;

        function initScene () {
            var canvas = horseCanvas.value;
            if (!canvas) return;

            var parent = canvas.parentElement,
                w = parent.offsetWidth,
                h = parent.offsetHeight;

            renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true });
            renderer.setSize(w, h);
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            renderer.toneMapping = THREE.ACESFilmicToneMapping;
            renderer.toneMappingExposure = 1.15;
            renderer.shadowMap.enabled = true;
            renderer.shadowMap.type = THREE.PCFSoftShadowMap;

            var scene = new THREE.Scene();

            // Camera — pushed right
            var camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 100);
            var isMobile = w < 680;
            camera.position.set(isMobile ? 2.2 : 3, isMobile ? 0.7 : 0.9, 5.5);
            camera.lookAt(isMobile ? -0.8 : -1.5, 0.2, 0);

            // Lighting
            scene.add(new THREE.AmbientLight(0xffffff, 0.7));
            var keyLight = new THREE.DirectionalLight(0xffffff, 1.5);
            keyLight.position.set(3, 6, 4);
            keyLight.castShadow = true;
            keyLight.shadow.mapSize.width = 2048;
            keyLight.shadow.mapSize.height = 2048;
            keyLight.shadow.camera.near = 0.5;
            keyLight.shadow.camera.far = 20;
            keyLight.shadow.camera.left = -4;
            keyLight.shadow.camera.right = 4;
            keyLight.shadow.camera.top = 4;
            keyLight.shadow.camera.bottom = -4;
            keyLight.shadow.bias = -0.001;
            scene.add(keyLight);
            var rimLight = new THREE.DirectionalLight(0x7c3aed, 0.4);
            rimLight.position.set(-4, 3, -3);
            scene.add(rimLight);

            // Mouse tracking — listen on window so overlapping DOM doesn't block
            var mouse = new THREE.Vector2(999, 999);
            var mouseSpeed = 0;
            var lastMX = 0, lastMY = 0;
            var raycaster = new THREE.Raycaster();
            function onMouseMove (e) {
                var rect = canvas.getBoundingClientRect();
                // Only track if mouse is over the canvas area
                if (e.clientX >= rect.left && e.clientX <= rect.right &&
                    e.clientY >= rect.top && e.clientY <= rect.bottom) {
                    var nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
                    var ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;
                    var dx = e.clientX - lastMX, dy = e.clientY - lastMY;
                    mouseSpeed = Math.min(1, Math.sqrt(dx*dx + dy*dy) * 0.02);
                    mouse.x = nx;
                    mouse.y = ny;
                } else {
                    mouse.x = 999;
                }
                lastMX = e.clientX;
                lastMY = e.clientY;
            }
            window.addEventListener("mousemove", onMouseMove);
            canvas._onMouseMove = onMouseMove;

            // Load model — pushed more right
            var modelGroup = new THREE.Group();
            modelGroup.position.x = 0;
            scene.add(modelGroup);
            var faceDataArr = [];
            var loader = new GLTFLoader();
            loader.load("/unicorn-lowpoly.glb", function (gltf) {
                var model = gltf.scene;
                var box = new THREE.Box3().setFromObject(model);
                var size = box.getSize(new THREE.Vector3());
                var center = box.getCenter(new THREE.Vector3());
                var maxDim = Math.max(size.x, size.y, size.z);
                // Scale relative to viewport — center-anchored
                var baseScale = Math.min(w, h) / 280;
                var s = baseScale / maxDim;
                model.scale.setScalar(s);
                // Center the model at origin (scale from middle)
                model.position.set(-center.x*s, -center.y*s, -center.z*s);
                model.userData.origMaxDim = maxDim;
                model.userData.origCenter = center.clone();
                faceDataArr = origamifyModel(model);
                modelGroup.add(model);

                // Shadow ground plane — separate from rotating group so it stays flat
                var groundGeo = new THREE.PlaneGeometry(10, 10);
                groundGeo.rotateX(-Math.PI / 2);
                var groundMat = new THREE.ShadowMaterial({ opacity: 0.3 });
                var ground = new THREE.Mesh(groundGeo, groundMat);
                ground.position.y = -size.y * s / 2;
                ground.position.x = modelGroup.position.x;
                ground.receiveShadow = true;
                scene.add(ground);

                // Enable shadow casting on all model meshes
                model.traverse(function (c) {
                    if (c.isMesh) c.castShadow = true;
                });

                modelGroup.rotation.y = -0.3;
            });

            var faceSpins = {};
            var clock = new THREE.Clock();
            var allMeshes = [];
            var PI2 = Math.PI * 2;

            function animate () {
                animId = requestAnimationFrame(animate);
                var t = clock.getElapsedTime();

                if (faceDataArr.length > 0) {
                    shimmerFaces(faceDataArr, t);

                    if (allMeshes.length === 0) {
                        modelGroup.traverse(function (c) { if (c.isMesh) allMeshes.push(c); });
                    }

                    // Raycast
                    if (mouse.x > -1.1 && mouse.x < 1.1) {
                        raycaster.setFromCamera(mouse, camera);
                        var hits = raycaster.intersectObjects(allMeshes, false);
                        if (hits.length > 0) {
                            var hit = hits[0];
                            var fi = hit.face ? Math.floor(hit.face.a / 3) : 0;
                            var sKey = hit.object.id + "_" + fi;
                            if (!faceSpins[sKey]) {
                                var pos = hit.object.geometry.attributes.position.array;
                                var idx = fi * 9;
                                if (idx + 8 < pos.length) {
                                    // Random 6DOF axis
                                    var ax=Math.random()-0.5, ay=Math.random()-0.5, az=Math.random()-0.5;
                                    var al=Math.sqrt(ax*ax+ay*ay+az*az);
                                    // Angular velocity: mouse speed capped for smooth motion
                                    var vel = Math.min(Math.max(mouseSpeed, 0.15) * 18, 12);
                                    faceSpins[sKey] = {
                                        orig: new Float32Array(pos.slice(idx, idx+9)),
                                        angle: 0, vel: vel,
                                        mesh: hit.object, fi: fi,
                                        ax: ax/al, ay: ay/al, az: az/al
                                    };
                                }
                            }
                        }
                    }

                    var keys = Object.keys(faceSpins);
                    for (var k = 0; k < keys.length; k++) {
                        var sp = faceSpins[keys[k]];

                        // Phase 1: free spin with light friction only
                        sp.vel *= 0.985;
                        sp.angle += sp.vel * 0.016;

                        // Phase 2: only when VERY slow, tiny nudge toward nearest full rev
                        var absVel = Math.abs(sp.vel);
                        if (absVel < 0.5) {
                            var remainder = sp.angle % PI2;
                            if (remainder < 0) remainder += PI2;
                            var target = sp.angle - remainder;
                            if (remainder > Math.PI) target += PI2;
                            var diff = target - sp.angle;
                            // Very gentle — just a tiny nudge, no reversal
                            sp.vel += diff * 0.5;
                        }

                        // Apply rotation from original positions
                        var pos = sp.mesh.geometry.attributes.position.array;
                        var idx = sp.fi * 9;
                        var o = sp.orig;
                        var cx=(o[0]+o[3]+o[6])/3, cy=(o[1]+o[4]+o[7])/3, cz=(o[2]+o[5]+o[8])/3;
                        var a=sp.angle, cosA=Math.cos(a), sinA=Math.sin(a);
                        var ux=sp.ax, uy=sp.ay, uz=sp.az;
                        for (var v=0; v<3; v++) {
                            var vi=v*3;
                            var dx=o[vi]-cx, dy=o[vi+1]-cy, dz=o[vi+2]-cz;
                            var dt=ux*dx+uy*dy+uz*dz;
                            pos[idx+vi]  =cx+dx*cosA+(uy*dz-uz*dy)*sinA+ux*dt*(1-cosA);
                            pos[idx+vi+1]=cy+dy*cosA+(uz*dx-ux*dz)*sinA+uy*dt*(1-cosA);
                            pos[idx+vi+2]=cz+dz*cosA+(ux*dy-uy*dx)*sinA+uz*dt*(1-cosA);
                        }
                        sp.mesh.geometry.attributes.position.needsUpdate = true;

                        // Remove when settled (near full revolution + nearly stopped)
                        var rem = Math.abs(sp.angle % PI2);
                        if (rem > Math.PI) rem = PI2 - rem;
                        if (absVel < 0.15 && rem < 0.05) {
                            for (var v=0;v<9;v++) pos[idx+v]=o[v];
                            sp.mesh.geometry.attributes.position.needsUpdate = true;
                            delete faceSpins[keys[k]];
                        }
                    }
                }

                mouseSpeed *= 0.9;
                modelGroup.rotation.y = -0.3 + Math.sin(t * 0.15) * 0.35;
                renderer.render(scene, camera);
            }
            animate();

            function onResize () {
                var pw = parent.offsetWidth, ph = parent.offsetHeight;
                var mob = pw < 680;
                camera.position.set(mob ? 2.2 : 3, mob ? 0.7 : 0.9, 5.5);
                camera.lookAt(mob ? -0.8 : -1.5, 0.2, 0);
                camera.aspect = pw / ph;
                camera.updateProjectionMatrix();
                renderer.setSize(pw, ph);
                modelGroup.traverse(function (c) {
                    if (c.userData && c.userData.origMaxDim) {
                        var ns = Math.min(pw, ph) / 280 / c.userData.origMaxDim;
                        c.scale.setScalar(ns);
                        c.position.set(
                            -c.userData.origCenter.x * ns,
                            -c.userData.origCenter.y * ns,
                            -c.userData.origCenter.z * ns
                        );
                    }
                });
            }
            window.addEventListener("resize", onResize);
            canvas._onResize = onResize;

            // Scroll parallax — canvas moves at 30% of scroll speed
            function onScroll () {
                var scrollY = window.scrollY || window.pageYOffset;
                canvas.style.transform = "translateY(" + (scrollY * 0.7) + "px)";
            }
            window.addEventListener("scroll", onScroll, { passive: true });
            canvas._onScroll = onScroll;
        }

        onMounted(function () {
            initScene();
        });

        onBeforeUnmount(function () {
            if (animId) cancelAnimationFrame(animId);
            if (renderer) renderer.dispose();
            var canvas = horseCanvas.value;
            if (canvas) {
                if (canvas._onResize) window.removeEventListener("resize", canvas._onResize);
                if (canvas._onMouseMove) window.removeEventListener("mousemove", canvas._onMouseMove);
                if (canvas._onScroll) window.removeEventListener("scroll", canvas._onScroll);
            }
        });

        return { "localize": translation.localize, getRawIcon, capabilities, deployments, horseCanvas };
    }
};
</script>
<style scoped>

.home {
  overflow-x: hidden;
  background: var(--bg-primary);
  font-family: var(--font-body);
  color: var(--text-primary);
  /* front page runs one step larger than the 15px app base */
  font-size: 17px;
}

/* =======================================================
   HERO — full viewport, angular shapes edge to edge
   ======================================================= */
.hero {
  position: relative;
  min-height: 100vh;
  overflow: visible;
}

/* ---- Three.js horse canvas ---- */
.horse-canvas {
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: auto;
  z-index: 1;
  cursor: default;
  position: absolute;
  filter: drop-shadow(0 0 18px rgba(251,191,36,0.2)) drop-shadow(0 0 40px rgba(236,72,153,0.12));
}

.hero-dots {
  position: absolute;
  top: 20%;
  right: 14%;
  width: 180px;
  height: 180px;
  background-image: radial-gradient(rgba(124,58,237,0.15) 1.5px, transparent 1.5px);
  background-size: 24px 24px;
  pointer-events: none;
}


.hero-content {
  position: relative;
  z-index: 3;
  max-width: 1200px;
  margin: 0 auto;
  padding: 180px 56px 0;
  min-height: 70vh;
  display: flex;
  align-items: center;
}

.hero-text { max-width: 580px; }

.hero-badge {
  display: inline-block;
  padding: 6px 64px;
  margin-bottom: 28px;
  margin-left: -40px;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 3.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.95);
  background: transparent;
  position: relative;
  text-shadow: 0 0 6px rgba(236,72,153,0.9), 0 0 16px rgba(124,58,237,0.6), 0 0 30px rgba(6,182,212,0.3);
}

/* Ellipse color backing */
.hero-badge::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 95%;
  height: 110%;
  transform: translate(-50%, -50%);
  background: radial-gradient(ellipse 50% 35% at center, rgba(236,72,153,0.95) 0%, rgba(124,58,237,0.85) 40%, rgba(6,182,212,0.6) 70%, transparent 100%);
  filter: blur(6px);
  border-radius: 50%;
  z-index: -1;
  pointer-events: none;
}

/* CRT shutdown line — thin bright accent */
.hero-badge::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  transform: translateY(-50%);
  background: linear-gradient(90deg, transparent 3%, rgba(236,72,153,0.6) 15%, rgba(255,255,255,0.7) 35%, rgba(255,255,255,0.7) 65%, rgba(6,182,212,0.6) 85%, transparent 97%);
  box-shadow: 0 0 6px rgba(236,72,153,0.6), 0 0 14px rgba(124,58,237,0.4);
}

.hero-title {
  font-family: var(--font-heading);
  font-size: 4.2em;
  font-weight: 800;
  line-height: 1.06;
  margin: 0 0 24px;
  letter-spacing: -2px;
  color: var(--text-primary);
}

.hero-desc {
  font-size: 1.15em;
  color: var(--text-secondary);
  line-height: 1.85;
  margin: 0 0 32px;
}

/* ---- Dual CTA: Defence (demo) vs Offensive (security audit) ---- */
.hero-cta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: stretch;
}

.cta-choice-wrap {
  position: relative;
  display: flex;
  flex: 1 1 250px;
  max-width: 330px;
}

.cta-choice {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  flex: 1 1 auto;
  width: 100%;
  padding: 18px 30px;
  border-radius: 20px;
  text-decoration: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}

.cta-choice-label {
  font-family: var(--font-heading);
  font-size: 1em;
  font-weight: 700;
  line-height: 1.3;
}

.cta-choice-hint {
  font-size: 0.78em;
  font-weight: 500;
  line-height: 1.45;
}

.cta-choice-defence {
  color: #fff;
  background: linear-gradient(135deg, #7c3aed 0%, #6366f1 45%, #06b6d4 100%);
  border: 1.5px solid transparent;
  box-shadow: 0 8px 32px rgba(99,102,241,0.35);
}

.cta-choice-defence .cta-choice-hint { color: rgba(255,255,255,0.82); }

.cta-choice-defence:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 44px rgba(6,182,212,0.42);
}

/* Red team card — same weight as the blue one, warm rose/pink end of the palette */
.cta-choice-offensive {
  color: #fff;
  background: linear-gradient(135deg, #fb7185 0%, #f43f5e 45%, #ec4899 100%);
  border: 1.5px solid transparent;
  box-shadow: 0 8px 32px rgba(244,63,94,0.32), inset 0 1px 0 rgba(255,255,255,0.12);
}

.cta-choice-offensive .cta-choice-label { color: #fff; }

.cta-choice-offensive .cta-choice-hint {
  color: rgba(255,255,255,0.8);
  font-family: var(--font-mono);
  font-size: 0.68em;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.cta-choice-offensive:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 44px rgba(236,72,153,0.45), inset 0 1px 0 rgba(255,255,255,0.16);
}

/* Live-agent pulse on the red team hint */
.cta-choice-pulse {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 8px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 0 0 rgba(255,255,255,0.7);
  animation: cta-pulse 2.4s ease-out infinite;
  vertical-align: middle;
}

@keyframes cta-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(255,255,255,0.6); }
  70%  { box-shadow: 0 0 0 8px rgba(255,255,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
}

/* Scan sweep across the red team card on hover */
.cta-choice-scan {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -60%;
  width: 45%;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.22), transparent);
  pointer-events: none;
  opacity: 0;
}

.cta-choice:hover .cta-choice-scan {
  opacity: 1;
  animation: cta-scan 1.5s ease-in-out infinite;
}

@keyframes cta-scan {
  0%   { left: -60%; }
  100% { left: 115%; }
}

/* Retro CRT overlay — scanlines, always on, intensified on hover */
.cta-choice-crt {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: repeating-linear-gradient(
    0deg,
    rgba(0,0,0,0.17) 0px,
    rgba(0,0,0,0.17) 1px,
    rgba(255,255,255,0.06) 1px,
    rgba(255,255,255,0.06) 2px,
    transparent 2px,
    transparent 4px
  );
  opacity: 0.7;
  transition: opacity 0.35s ease;
}

.cta-choice-offensive:hover .cta-choice-crt { opacity: 1; }

/* the bright band that rolls down a CRT that is slightly out of sync */
.cta-choice-crt::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: -35%;
  height: 32%;
  background: linear-gradient(180deg, transparent, rgba(255,255,255,0.18), transparent);
  opacity: 0;
}

.cta-choice-offensive:hover .cta-choice-crt::after {
  opacity: 1;
  animation: cta-roll 2.6s linear infinite;
}

@keyframes cta-roll {
  0%   { top: -35%; }
  100% { top: 100%; }
}

/* Hover reveal — the long pitch, floated above the button */
.cta-reveal {
  position: absolute;
  bottom: calc(100% + 12px);
  left: 50%;
  margin-left: -170px;
  width: 340px;
  max-width: 88vw;
  z-index: 12;
  padding: 16px 20px;
  border-radius: 16px;
  font-size: 0.8em;
  line-height: 1.65;
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-subtle);
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
  opacity: 0;
  visibility: hidden;
  transform: translateY(8px);
  transition: opacity 0.28s ease, transform 0.28s ease, visibility 0.28s;
  pointer-events: none;
}

.cta-reveal::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -7px;
  border: 7px solid transparent;
  border-top-color: var(--border-subtle);
}

.cta-reveal-red {
  border-color: rgba(244,63,94,0.45);
  box-shadow: 0 20px 60px rgba(244,63,94,0.22);
}

.cta-reveal-red::after { border-top-color: rgba(244,63,94,0.45); }

.cta-choice-wrap:hover .cta-reveal,
.cta-choice-wrap:focus-within .cta-reveal {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

/* Lift the hero text above the overlapping zero cards while a panel is open */
.hero-content:has(.cta-choice-wrap:hover),
.hero-content:has(.cta-choice-wrap:focus-within) { z-index: 6; }

@media (prefers-reduced-motion: reduce) {
  .cta-choice-pulse { animation: none; }
  .cta-choice:hover .cta-choice-scan { animation: none; opacity: 0; }
  .cta-choice-offensive:hover .cta-choice-crt::after { animation: none; opacity: 0; }
  .cta-reveal { transition: opacity 0.01s linear; transform: none; }
}

/* Zero cards — positioned to OVERLAP the hero bottom, breaking the boundary */
.hero-zeros-wrap {
  position: relative;
  z-index: 4;
  margin-top: -20px;
  padding: 0 56px 0;
}

.hero-zeros {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  transform: translateY(50%);
}

.zero-card {
  background: #fff;
  border-radius: 18px;
  padding: 34px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 14px 50px rgba(124,58,237,0.18);
  border: 1px solid rgba(124,58,237,0.06);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
  position: relative;
  overflow: hidden;
}

.zero-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4);
}

.zero-card:nth-child(2)::after { background: linear-gradient(90deg, #ec4899, #fbbf24); }
.zero-card:nth-child(3)::after { background: linear-gradient(90deg, #06b6d4, #7c3aed); }
.zero-card:nth-child(4)::after { background: linear-gradient(90deg, #fbbf24, #ec4899); }

.zero-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 24px 64px rgba(124,58,237,0.28);
}

.zero-value {
  font-family: var(--font-heading);
  font-size: 3em;
  font-weight: 800;
  letter-spacing: -1px;
  background: linear-gradient(135deg, #7c3aed, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.zero-card:nth-child(2) .zero-value { background: linear-gradient(135deg, #ec4899, #fbbf24); -webkit-background-clip: text; background-clip: text; }
.zero-card:nth-child(3) .zero-value { background: linear-gradient(135deg, #06b6d4, #7c3aed); -webkit-background-clip: text; background-clip: text; }
.zero-card:nth-child(4) .zero-value { background: linear-gradient(135deg, #fbbf24, #ec4899); -webkit-background-clip: text; background-clip: text; }

.zero-label {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: var(--text-secondary);
}

/* =======================================================
   SHARED LAYOUT
   ======================================================= */
.section {
  position: relative;
  padding: 100px 56px;
  z-index: 3;
}

.section-stats {
  padding-top: 160px; /* extra space for overlapping zero cards */
}

.section-wrap {
  max-width: 1100px;
  margin: 0 auto;
}

.section-wrap-narrow {
  max-width: 800px;
}

/* Full-bleed gradient sections with angular clip */
.section-full {
  position: relative;
  padding: 140px 56px;
  overflow: hidden;
  z-index: 3;
}

.section-purple {
  background: linear-gradient(135deg, #7c3aed, #6366f1 50%, #5b21b6);
  clip-path: polygon(0 80px, 100% 0, 100% calc(100% - 80px), 0 100%);
  margin: -40px 0;
}

.section-cyan {
  background: linear-gradient(135deg, #06b6d4, #0891b2 40%, #7c3aed);
  clip-path: polygon(0 0, 100% 80px, 100% 100%, 0 calc(100% - 80px));
  margin: -40px 0;
}

.section-warm {
  background: linear-gradient(135deg, #7c3aed, #ec4899 60%, #f43f5e);
  clip-path: polygon(0 80px, 100% 0, 100% calc(100% - 80px), 0 100%);
  margin: -40px 0;
}

/* Geo shapes on gradient sections — bigger, bolder */
.section-full-geo {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.geo {
  position: absolute;
  background: rgba(255,255,255,0.08);
}

.geo-a { width: 600px; height: 600px; top: -180px; right: -100px; clip-path: polygon(0 0, 100% 0, 100% 100%); }
.geo-b { width: 400px; height: 400px; bottom: -120px; left: 2%; clip-path: polygon(0 100%, 50% 0, 100% 100%); background: rgba(255,255,255,0.05); }
.geo-c { width: 250px; height: 250px; top: 25%; left: 6%; clip-path: polygon(50% 0, 100% 50%, 50% 100%, 0 50%); background: rgba(255,255,255,0.06); }
.geo-d { width: 550px; height: 550px; top: -200px; left: -120px; clip-path: polygon(0 0, 100% 25%, 80% 100%, 0 85%); }
.geo-e { width: 320px; height: 320px; bottom: 5%; right: 4%; clip-path: polygon(50% 0, 100% 50%, 50% 100%, 0 50%); background: rgba(255,255,255,0.05); }
.geo-f { width: 500px; height: 500px; top: -100px; right: -80px; clip-path: polygon(15% 0, 100% 0, 100% 85%); }
.geo-g { width: 380px; height: 380px; bottom: -80px; left: 5%; clip-path: polygon(0 0, 100% 40%, 0 100%); background: rgba(255,255,255,0.05); }

/* Badges & Titles */
.section-badge {
  display: inline-block;
  padding: 6px 64px;
  margin-left: -40px;
  margin-bottom: 20px;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 3.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.95);
  background: transparent;
  position: relative;
  text-shadow: 0 0 6px rgba(124,58,237,0.9), 0 0 16px rgba(6,182,212,0.5), 0 0 30px rgba(124,58,237,0.3);
}

.section-badge::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 95%;
  height: 110%;
  transform: translate(-50%, -50%);
  background: radial-gradient(ellipse 50% 35% at center, rgba(124,58,237,0.95) 0%, rgba(91,33,182,0.85) 40%, rgba(6,182,212,0.6) 70%, transparent 100%);
  filter: blur(6px);
  border-radius: 50%;
  z-index: -1;
  pointer-events: none;
}

.section-badge::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  transform: translateY(-50%);
  background: linear-gradient(90deg, transparent 3%, rgba(124,58,237,0.6) 15%, rgba(255,255,255,0.6) 35%, rgba(255,255,255,0.6) 65%, rgba(6,182,212,0.6) 85%, transparent 97%);
  box-shadow: 0 0 6px rgba(124,58,237,0.5), 0 0 14px rgba(6,182,212,0.3);
}

.section-badge-light {
  color: rgba(255,255,255,0.95);
  text-shadow: 0 0 6px rgba(255,255,255,0.8), 0 0 16px rgba(255,255,255,0.4), 0 0 30px rgba(255,255,255,0.2);
}

.section-badge-light::before {
  background: linear-gradient(90deg, transparent 3%, rgba(255,255,255,0.4) 15%, rgba(255,255,255,0.6) 35%, rgba(255,255,255,0.6) 65%, rgba(255,255,255,0.4) 85%, transparent 97%);
  box-shadow: 0 0 6px rgba(255,255,255,0.4), 0 0 14px rgba(255,255,255,0.2);
}

.section-badge-light::after {
  background: radial-gradient(ellipse 50% 35% at center, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
}

.section-title {
  font-family: var(--font-heading);
  font-size: 3em;
  font-weight: 800;
  line-height: 1.12;
  margin: 0 0 20px;
  letter-spacing: -1px;
  background: linear-gradient(135deg, #5b21b6, #7c3aed 30%, #6366f1 60%, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-title-light {
  background: none;
  -webkit-text-fill-color: #fff;
  color: #fff;
  text-shadow: 0 2px 20px rgba(0,0,0,0.15);
}

.section-subtitle { font-size: 1.15em; color: var(--text-secondary); line-height: 1.75; margin: 0 0 48px; max-width: 640px; }
.section-sub-light { color: rgba(255,255,255,0.8); }

/* =======================================================
   STAT CARDS
   ======================================================= */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px;
}

.stat-card {
  background: #fff;
  border-radius: 20px;
  padding: 44px 36px;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(124,58,237,0.06);
  box-shadow: 0 10px 50px rgba(124,58,237,0.12);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
}

/* Accent bar that extrudes from the left edge */
.stat-accent {
  position: absolute;
  left: 0;
  top: 20px;
  bottom: 20px;
  width: 5px;
  background: linear-gradient(180deg, #7c3aed, #06b6d4);
  border-radius: 0 4px 4px 0;
}

.stat-card:nth-child(2) .stat-accent { background: linear-gradient(180deg, #ec4899, #fbbf24); }
.stat-card:nth-child(3) .stat-accent { background: linear-gradient(180deg, #06b6d4, #7c3aed); }
.stat-card:nth-child(4) .stat-accent { background: linear-gradient(180deg, #fbbf24, #ec4899); }

.stat-card:hover {
  transform: translateY(-8px) rotate(-0.5deg);
  box-shadow: 0 24px 64px rgba(124,58,237,0.22);
}

.stat-number {
  font-family: var(--font-heading);
  font-size: 3.8em;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #7c3aed, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-card:nth-child(2) .stat-number { background: linear-gradient(135deg, #ec4899, #fbbf24); -webkit-background-clip: text; background-clip: text; }
.stat-card:nth-child(3) .stat-number { background: linear-gradient(135deg, #06b6d4, #7c3aed); -webkit-background-clip: text; background-clip: text; }
.stat-card:nth-child(4) .stat-number { background: linear-gradient(135deg, #fbbf24, #ec4899); -webkit-background-clip: text; background-clip: text; }

.stat-label { font-family: var(--font-heading); font-size: 1.15em; font-weight: 700; color: var(--text-primary); margin-bottom: 10px; }
.stat-desc { font-size: 0.98em; color: var(--text-secondary); line-height: 1.7; }

/* =======================================================
   COMPARE
   ======================================================= */
.compare-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
}

.compare-col {
  background: #fff;
  border-radius: 20px;
  padding: 44px 36px;
  box-shadow: 0 16px 50px rgba(0,0,0,0.18);
  transition: transform 0.35s ease;
}

.compare-col:hover { transform: translateY(-6px); }
.compare-before { opacity: 0.9; }

/* After card extrudes downward */
.compare-after {
  transform: translateY(30px);
  z-index: 2;
  position: relative;
}

.compare-after:hover { transform: translateY(22px); }

.compare-header {
  font-family: var(--font-heading);
  font-size: 1.25em;
  font-weight: 800;
  margin-bottom: 28px;
  padding-bottom: 16px;
  border-bottom: 3px solid #e9e5f0;
}

.compare-header-before { color: #8b83a0; }
.compare-header-after {
  background: linear-gradient(90deg, #7c3aed, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  border-bottom-color: #7c3aed;
}

.compare-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
.compare-item { display: flex; align-items: flex-start; gap: 14px; font-size: 0.94em; line-height: 1.6; color: #4a4458; }
.compare-bullet { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; margin-top: 6px; }
.bullet-before { background: #c4b5d0; }
.bullet-after { background: linear-gradient(135deg, #7c3aed, #06b6d4); box-shadow: 0 0 12px rgba(124,58,237,0.5); }

/* =======================================================
   SIMPLICITY
   ======================================================= */
.simplicity-card {
  background: #fff;
  border-radius: 20px;
  padding: 52px 48px 52px 56px;
  border: 1px solid rgba(124,58,237,0.06);
  box-shadow: 0 10px 50px rgba(124,58,237,0.12);
  position: relative;
  overflow: visible;
}

/* Stripe that extrudes to the left beyond the card */
.simplicity-stripe {
  position: absolute;
  left: -16px;
  top: 24px;
  bottom: 24px;
  width: 8px;
  background: linear-gradient(180deg, #7c3aed, #06b6d4, #ec4899);
  border-radius: 4px;
  box-shadow: 0 0 20px rgba(124,58,237,0.4);
}

.simplicity-lead { font-size: 1.1em; color: var(--text-secondary); line-height: 1.85; margin: 0 0 28px; }

.simplicity-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #fbbf24, #ec4899);
  margin-bottom: 28px;
  border-radius: 2px;
  box-shadow: 0 0 16px rgba(236,72,153,0.4);
}

.simplicity-points { display: flex; flex-direction: column; gap: 16px; }
.simplicity-point { display: flex; align-items: flex-start; gap: 16px; font-size: 0.98em; color: var(--text-secondary); line-height: 1.7; }
.simplicity-bullet { width: 10px; height: 10px; background: linear-gradient(135deg, #7c3aed, #06b6d4); flex-shrink: 0; margin-top: 7px; border-radius: 50%; box-shadow: 0 0 8px rgba(124,58,237,0.3); }

/* =======================================================
   CAP CARDS
   ======================================================= */
.cap-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.cap-card {
  background: #fff;
  border-radius: 20px;
  padding: 36px 28px;
  box-shadow: 0 14px 50px rgba(0,0,0,0.18);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
  position: relative;
  overflow: hidden;
}

/* Every other card pops up to create an uneven, interesting grid */
.cap-card:nth-child(2) { transform: translateY(-16px); }
.cap-card:nth-child(5) { transform: translateY(-16px); }

.cap-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4);
  border-radius: 0 0 20px 20px;
}

.cap-card:nth-child(2)::after { background: linear-gradient(90deg, #ec4899, #fbbf24); }
.cap-card:nth-child(3)::after { background: linear-gradient(90deg, #06b6d4, #ec4899); }
.cap-card:nth-child(4)::after { background: linear-gradient(90deg, #fbbf24, #7c3aed); }
.cap-card:nth-child(5)::after { background: linear-gradient(90deg, #7c3aed, #ec4899); }
.cap-card:nth-child(6)::after { background: linear-gradient(90deg, #06b6d4, #fbbf24); }

.cap-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 24px 64px rgba(0,0,0,0.28);
}

.cap-card:nth-child(2):hover,
.cap-card:nth-child(5):hover { transform: translateY(-26px); }

.cap-icon {
  width: 52px;
  height: 52px;
  margin-bottom: 20px;
  color: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(124,58,237,0.12), rgba(6,182,212,0.08));
  border-radius: 14px;
  padding: 12px;
}

.cap-icon :deep(svg) { width: 28px; height: 28px; }
.cap-name { font-family: var(--font-heading); font-size: 1.1em; font-weight: 700; color: var(--text-primary); margin: 0 0 12px; }
.cap-desc { font-size: 0.98em; color: var(--text-secondary); line-height: 1.7; margin: 0; }

/* =======================================================
   DIFF TABLE
   ======================================================= */
.diff-card {
  background: #fff;
  border-radius: 20px;
  padding: 16px;
  overflow: hidden;
  border: 1px solid rgba(124,58,237,0.06);
  box-shadow: 0 10px 50px rgba(124,58,237,0.12);
}

.diff-table { width: 100%; }
.diff-header-row, .diff-row { display: grid; grid-template-columns: 1.4fr 1fr 1fr; }
.diff-header-row { background: linear-gradient(90deg, #7c3aed, #6366f1, #06b6d4); border-radius: 12px; margin-bottom: 8px; }
.diff-header-row .diff-feature-col { visibility: hidden; }
.diff-header-row .diff-col { font-family: var(--font-heading); font-size: 0.88em; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; padding: 18px 22px; color: #fff; }
.diff-header-row .diff-col-trad { color: rgba(255,255,255,0.7); }
.diff-col-trad { color: var(--text-secondary); }
.diff-row { border-bottom: 1px solid rgba(124,58,237,0.06); transition: background 0.3s ease; border-radius: 8px; }
.diff-row:last-child { border-bottom: none; }
.diff-row:hover { background: rgba(124,58,237,0.04); }
.diff-feature-col { font-size: 1em; font-weight: 600; color: var(--text-primary); padding: 16px 22px; display: flex; align-items: center; }
.diff-row .diff-col { font-size: 0.98em; color: var(--text-secondary); padding: 16px 22px; display: flex; align-items: center; line-height: 1.5; }
.diff-row .diff-col-enbarr { color: #7c3aed; font-weight: 700; }

/* =======================================================
   DEPLOY CARDS
   ======================================================= */
.deploy-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.deploy-card {
  background: #fff;
  border-radius: 20px;
  padding: 48px 32px;
  text-align: center;
  box-shadow: 0 14px 50px rgba(0,0,0,0.18);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
}

/* Middle card pops up */
.deploy-card:nth-child(2) { transform: translateY(-20px); }
.deploy-card:nth-child(2):hover { transform: translateY(-32px); }

.deploy-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 24px 64px rgba(0,0,0,0.28);
}

.deploy-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 22px;
  color: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(124,58,237,0.12), rgba(6,182,212,0.08));
  border-radius: 16px;
  padding: 14px;
}

.deploy-icon :deep(svg) { width: 32px; height: 32px; }
.deploy-name { font-family: var(--font-heading); font-size: 1.2em; font-weight: 700; color: var(--text-primary); margin: 0 0 12px; }
.deploy-desc { font-size: 0.98em; color: var(--text-secondary); line-height: 1.75; margin: 0; }

/* =======================================================
   CTA
   ======================================================= */
.section-cta { padding: 80px 56px 120px; }

.cta-heading {
  font-family: var(--font-heading);
  font-size: 3em;
  font-weight: 800;
  line-height: 1.12;
  margin: 0 0 20px;
  text-align: center;
  background: linear-gradient(135deg, #5b21b6, #7c3aed 30%, #6366f1 60%, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.cta-subtext {
  font-size: 1.1em;
  font-style: italic;
  color: var(--text-secondary);
  line-height: 1.8;
  margin: 0 auto 36px;
  max-width: 520px;
  text-align: center;
}

.cta-card {
  background: linear-gradient(135deg, #7c3aed, #6366f1 40%, #06b6d4);
  border-radius: 28px;
  text-align: center;
  padding: 48px 56px;
  position: relative;
  box-shadow: 0 24px 80px rgba(124,58,237,0.35);
}

/* clips the angular extrusions without clipping the hover panel */
.cta-card-geo {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: inherit;
}

/* Angular extrusions inside the CTA */
.cta-geo-1 {
  position: absolute;
  top: -60px;
  right: -40px;
  width: 350px;
  height: 350px;
  background: rgba(255,255,255,0.08);
  clip-path: polygon(20% 0, 100% 0, 100% 80%, 0 100%);
}

.cta-geo-2 {
  position: absolute;
  bottom: -40px;
  left: -30px;
  width: 280px;
  height: 280px;
  background: rgba(255,255,255,0.05);
  clip-path: polygon(0 15%, 100% 0, 85% 100%, 0 100%);
}

.cta-title {
  font-family: var(--font-heading);
  font-size: 2.8em;
  font-weight: 800;
  line-height: 1.12;
  margin: 0 0 24px;
  color: #fff;
  text-shadow: 0 2px 15px rgba(0,0,0,0.15);
  position: relative;
  z-index: 1;
}

.cta-quote {
  font-size: 1.1em;
  font-style: italic;
  color: rgba(255,255,255,0.85);
  line-height: 1.8;
  margin: 0 auto 40px;
  max-width: 520px;
  position: relative;
  z-index: 1;
}

.cta-button {
  display: inline-block;
  padding: 18px 52px;
  font-family: var(--font-heading);
  font-size: 1em;
  font-weight: 700;
  color: #7c3aed;
  background: #fff;
  border: none;
  border-radius: 30px;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 10px 36px rgba(0,0,0,0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  z-index: 1;
}

.cta-button:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 48px rgba(0,0,0,0.3);
}

.cta-button-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.cta-button-ghost {
  color: #fff;
  background: rgba(255,255,255,0.12);
  border: 1.5px solid rgba(255,255,255,0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: none;
}

.cta-button-ghost:hover {
  background: rgba(255,255,255,0.2);
  box-shadow: 0 16px 48px rgba(0,0,0,0.25);
}

.cta-choice-wrap-inline {
  flex: 0 0 auto;
  max-width: none;
}

.cta-reveal-dark {
  border-color: rgba(244,63,94,0.45);
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  text-align: center;
}

.cta-reveal-dark::after { border-top-color: rgba(244,63,94,0.45); }

/* =======================================================
   DARK MODE
   ======================================================= */
:root[data-theme="dark"] .horse-canvas { opacity: 0.8; }

:root[data-theme="dark"] .zero-card,
:root[data-theme="dark"] .stat-card,
:root[data-theme="dark"] .simplicity-card,
:root[data-theme="dark"] .diff-card {
  background: rgba(20,15,40,0.92);
  border-color: rgba(124,58,237,0.2);
  box-shadow: 0 10px 50px rgba(0,0,0,0.35);
}

:root[data-theme="dark"] .compare-col,
:root[data-theme="dark"] .cap-card,
:root[data-theme="dark"] .deploy-card {
  background: rgba(20,15,40,0.92);
  border: 1px solid rgba(124,58,237,0.2);
  box-shadow: 0 14px 50px rgba(0,0,0,0.35);
}

:root[data-theme="dark"] .compare-item { color: rgba(255,255,255,0.7); }
:root[data-theme="dark"] .compare-header { border-bottom-color: rgba(124,58,237,0.2); }
:root[data-theme="dark"] .cap-icon,
:root[data-theme="dark"] .deploy-icon { background: linear-gradient(135deg, rgba(124,58,237,0.2), rgba(6,182,212,0.15)); color: #a78bfa; }
:root[data-theme="dark"] .diff-header-row { background: linear-gradient(90deg, #5b21b6, #4f46e5, #0891b2); }
:root[data-theme="dark"] .diff-row .diff-col-enbarr { color: #a78bfa; }
:root[data-theme="dark"] .section-purple { background: linear-gradient(135deg, #4c1d95, #3730a3 50%, #312e81); }
:root[data-theme="dark"] .section-cyan { background: linear-gradient(135deg, #0e7490, #155e75 40%, #4c1d95); }
:root[data-theme="dark"] .section-warm { background: linear-gradient(135deg, #4c1d95, #9d174d 60%, #881337); }
:root[data-theme="dark"] .cta-card { background: linear-gradient(135deg, #4c1d95, #3730a3 40%, #0e7490); box-shadow: 0 24px 80px rgba(0,0,0,0.5); }
:root[data-theme="dark"] .section-title { background: linear-gradient(135deg, #a78bfa, #818cf8 40%, #22d3ee); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }

/* =======================================================
   RESPONSIVE
   ======================================================= */
@media (max-width: 1000px) {
  .hero-content { padding: 160px 36px 0; }
  .hero-zeros-wrap { padding: 0 36px; }
  .hero-title { font-size: 3.2em; }
  .hero-zeros { grid-template-columns: repeat(2, 1fr); }
  .horse-canvas { width: 100%; }
}

@media (max-width: 900px) {
  .hero-cta-row { flex-direction: column; }
  .cta-choice-wrap { max-width: 340px; }
  .section, .section-full { padding-left: 28px; padding-right: 28px; }
  .section-cta { padding-left: 28px; padding-right: 28px; }
  .stat-grid { grid-template-columns: 1fr 1fr; }
  .cap-grid { grid-template-columns: 1fr 1fr; }
  .cap-card:nth-child(2), .cap-card:nth-child(5) { transform: none; }
  .cap-card:nth-child(2):hover, .cap-card:nth-child(5):hover { transform: translateY(-10px); }
  .deploy-grid { grid-template-columns: 1fr; max-width: 420px; margin: 0 auto; }
  .deploy-card:nth-child(2) { transform: none; }
  .deploy-card:nth-child(2):hover { transform: translateY(-10px); }
  .section-title { font-size: 2.4em; }
  .section-full { clip-path: polygon(0 50px, 100% 0, 100% calc(100% - 50px), 0 100%); }
}

@media (max-width: 680px) {
  .hero { min-height: auto; }
  .hero-content { padding: 120px 20px 0; min-height: auto; }
  .hero-zeros-wrap { padding: 0 20px; }
  .hero-text { max-width: 55%; }
  .hero-title { font-size: 2em; letter-spacing: -1px; }
  .hero-desc { font-size: 0.9em; }
  .horse-canvas { width: 100%; opacity: 1; }
  .hero-dots { display: none; }
  .hero-zeros { grid-template-columns: 1fr 1fr; gap: 12px; }
  .zero-card { padding: 22px 16px; }
  .zero-value { font-size: 1.6em; }
  .section, .section-full { padding: 64px 16px; }
  .section-stats { padding-top: 120px; }
  .section-full { clip-path: polygon(0 30px, 100% 0, 100% calc(100% - 30px), 0 100%); }
  .section-title { font-size: 2em; }
  .stat-grid { grid-template-columns: 1fr; }
  .stat-number { font-size: 3em; }
  .compare-grid { grid-template-columns: 1fr; }
  .compare-after { transform: none; }
  .compare-after:hover { transform: translateY(-6px); }
  .cap-grid { grid-template-columns: 1fr; }
  .simplicity-card { padding: 32px 32px 32px 40px; }
  .simplicity-stripe { left: -8px; }
  .cta-card { padding: 52px 24px; border-radius: 20px; }
  .cta-title { font-size: 1.8em; }
  .cta-choice-wrap { max-width: none; }
  .cta-choice { max-width: none; padding: 14px 20px; border-radius: 16px; }
  .cta-reveal { left: 0; right: 0; width: auto; max-width: none; margin-left: 0; font-size: 0.74em; padding: 12px 16px; }
  .cta-choice-label { font-size: 0.92em; }
  .cta-choice-hint { font-size: 0.72em; }
  .cta-button-row { flex-direction: column; align-items: stretch; }
  .cta-button, .cta-button-ghost { padding: 16px 24px; font-size: 0.92em; }
  .section-cta { padding: 60px 16px 80px; }
  .diff-header-row, .diff-row { grid-template-columns: 1fr; }
  .diff-header-row { border-radius: 8px; }
  .diff-header-row .diff-feature-col { display: none; }
  .diff-feature-col { font-weight: 700; padding: 14px 20px 4px; border-top: 1px solid rgba(124,58,237,0.06); }
  .diff-row .diff-col { padding: 4px 20px 6px; }
  .diff-row .diff-col::before { font-family: var(--font-mono); font-size: 10px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; margin-right: 8px; }
  .diff-row .diff-col-trad::before { content: 'Legacy: '; color: var(--text-secondary); }
  .diff-row .diff-col-enbarr::before { content: 'Enbarr: '; }
  .diff-row .diff-col:last-child { padding-bottom: 14px; }
}

/* RTL — only on text containers, layout untouched */
:global(.is-rtl) .hero-text,
:global(.is-rtl) .section-wrap,
:global(.is-rtl) .stat-card,
:global(.is-rtl) .compare-col,
:global(.is-rtl) .simplicity-card,
:global(.is-rtl) .cap-card,
:global(.is-rtl) .diff-card,
:global(.is-rtl) .deploy-card,
:global(.is-rtl) .zero-card,
:global(.is-rtl) .cta-heading,
:global(.is-rtl) .cta-subtext {
  direction: rtl;
  text-align: right;
}

</style>
